import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import {
  AlertCircle,
  Check,
  ChevronDown,
  KeyRound,
  Server,
  Trash2,
  Upload,
  Wrench,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { type ByokScopeState, useByokKeys } from "@/hooks/use-byok-keys";

interface ByokKeysPanelProps {
  getToken: () => Promise<string>;
}

function statusBadge(
  state: ByokScopeState,
  t: ReturnType<typeof useTranslation>["t"],
) {
  if (!state.configured) {
    return <Badge variant="outline">{t("connections:byok.notConfigured")}</Badge>;
  }
  if (state.validation_status === "active") {
    return (
      <Badge className="gap-1">
        <Check className="size-3" />
        {t("connections:byok.active")}
      </Badge>
    );
  }
  if (state.validation_status === "invalid") {
    return <Badge variant="destructive">{t("connections:byok.invalid")}</Badge>;
  }
  if (state.validation_status === "unknown") {
    return <Badge variant="secondary">{t("connections:byok.unknownStatus")}</Badge>;
  }
  return <Badge variant="secondary">{t("connections:byok.unverified")}</Badge>;
}

function keyPrecedenceHint(
  user: ByokScopeState,
  organization: ByokScopeState,
  environment: ByokScopeState,
  t: ReturnType<typeof useTranslation>["t"],
) {
  if (user.configured && environment.configured) {
    return t("connections:byok.userKeyTakesPrecedenceOverEnvironment");
  }
  if (organization.configured && environment.configured) {
    return t("connections:byok.organizationKeyTakesPrecedenceOverEnvironment");
  }
  if (environment.configured) {
    return t("connections:byok.environmentKeyHint");
  }
  return organization.configured
    ? t("connections:byok.userKeyTakesPrecedence")
    : t("connections:byok.organizationKeyHint");
}

/** User-owned BYOK settings. Stored values are deliberately never rendered. */
export function ByokKeysPanel({ getToken }: ByokKeysPanelProps) {
  const { t } = useTranslation();
  const { availability, providers, loading, error, refresh, save, remove, validate } = useByokKeys(getToken);
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [testing, setTesting] = useState<string | null>(null);

  const saveKey = useCallback(async (provider: string) => {
    const value = values[provider]?.trim();
    if (!value) return;
    setSaving(provider);
    try {
      const saved = await save(provider, value);
      if (saved) setValues((current) => ({ ...current, [provider]: "" }));
    } finally {
      setSaving(null);
    }
  }, [save, values]);

  const removeKey = useCallback(async (provider: string) => {
    setRemoving(provider);
    try {
      await remove(provider);
    } finally {
      setRemoving(null);
    }
  }, [remove]);

  const testKey = useCallback(async (provider: string) => {
    setTesting(provider);
    try {
      await validate(provider);
    } finally {
      setTesting(null);
    }
  }, [validate]);

  return (
    <div className="bg-card" data-testid="byok-keys-panel">
      {availability === "loading" && providers.length === 0 && (
        <div
          className="flex items-center justify-center py-6"
          aria-label={t("connections:byok.loading")}
        >
          <Spinner className="size-5" />
        </div>
      )}

      {availability === "unsupported" && (
        <div className="p-4" role="status">
          <div className="flex gap-3 text-sm">
            <AlertCircle className="mt-0.5 size-4 shrink-0 text-amber-600" />
            <div>
              <p className="font-medium">{t("connections:byok.unavailable")}</p>
              <p className="mt-1 text-muted-foreground">
                {t("connections:byok.unavailableDescription")}
              </p>
            </div>
          </div>
        </div>
      )}

      {availability === "error" && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 p-4 text-sm text-destructive"
          role="alert"
        >
          <span>{error ?? t("connections:byok.couldNotLoad")}</span>
          <Button variant="outline" size="sm" onClick={() => void refresh()}>
            {t("common:actions.retry")}
          </Button>
        </div>
      )}

      {availability === "supported" && (
        <>
          {error && (
            <div
              className="flex flex-wrap items-center justify-between gap-3 p-4 text-sm text-destructive"
              role="alert"
            >
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={() => void refresh()}>
                {t("common:actions.retry")}
              </Button>
            </div>
          )}
          <div
            className="divide-y"
            role="list"
            aria-label={t("connections:byok.title")}
            data-testid="byok-provider-list"
          >
            {providers.map((provider) => {
              const value = values[provider.provider] ?? "";
              const isSaving = saving === provider.provider;
              const isRemoving = removing === provider.provider;
              const isTesting = testing === provider.provider;
              const busy = isSaving || isRemoving || isTesting;

              return (
                <Collapsible key={provider.provider} asChild>
                  <article className="bg-card" data-byok-provider={provider.provider} role="listitem">
                    <div className="flex flex-col gap-4 p-4 sm:grid sm:grid-cols-[minmax(10rem,1fr)_fit-content(15.5rem)_auto] sm:items-start">
                      <div className="flex min-w-0 items-start gap-3">
                        <div
                          className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"
                          aria-hidden="true"
                        >
                          <KeyRound className="size-4" />
                        </div>
                        <div className="min-w-0 space-y-1">
                          <h3 className="font-medium text-foreground">{provider.label}</h3>
                          {provider.models.length > 0 && (
                            <p className="text-sm text-muted-foreground">
                              {t("connections:byok.models", { models: provider.models.join(", ") })}
                            </p>
                          )}
                        </div>
                      </div>

                      <div
                        className="min-w-0 text-left sm:text-right"
                        data-testid="byok-provider-badge-column"
                      >
                        <div className="flex flex-col items-start gap-1.5 sm:items-end">
                          {statusBadge(provider.user, t)}
                          {provider.organization.configured && (
                            <Badge variant="outline">
                              {t("connections:byok.organizationKeyAvailable")}
                            </Badge>
                          )}
                          {provider.environment.configured && (
                            <Badge variant="outline" className="gap-1">
                              <Server className="size-3" />
                              {t("connections:byok.environmentKeyAvailable")}
                            </Badge>
                          )}
                        </div>
                      </div>
                      {/* The shared CollapsibleTrigger defaults to w-full; this action must stay compact. */}
                      <CollapsiblePrimitive.Trigger asChild>
                        <Button
                          type="button"
                          size="sm"
                          className="self-start justify-self-end w-auto shrink-0 justify-center [&[data-state=open]>svg.chevron]:rotate-180"
                          aria-label={t("connections:byok.configureProvider", {
                            provider: provider.label,
                          })}
                        >
                          {t("connections:byok.configure")}
                          <ChevronDown className="chevron size-4 transition-transform" />
                        </Button>
                      </CollapsiblePrimitive.Trigger>
                    </div>

                    <CollapsibleContent>
                      <div className="space-y-4 border-t bg-muted/20 px-4 py-4">
                        <div className="flex flex-col gap-2 sm:flex-row">
                          <Input
                            type="password"
                            autoComplete="off"
                            aria-label={t("connections:byok.keyAriaLabel", {
                              provider: provider.label,
                            })}
                            placeholder={provider.user.configured
                              ? t("connections:byok.replaceKeyPlaceholder")
                              : t("connections:byok.enterKeyPlaceholder")}
                            value={value}
                            onChange={(event) => setValues((current) => ({
                              ...current,
                              [provider.provider]: event.target.value,
                            }))}
                            disabled={busy}
                          />
                          <Button
                            size="sm"
                            onClick={() => void saveKey(provider.provider)}
                            disabled={!value.trim() || busy}
                          >
                            <Upload className="size-3" />
                            {isSaving
                              ? t("connections:byok.saving")
                              : provider.user.configured
                                ? t("connections:byok.replace")
                                : t("connections:byok.save")}
                          </Button>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-xs text-muted-foreground">
                            {keyPrecedenceHint(
                              provider.user,
                              provider.organization,
                              provider.environment,
                              t,
                            )}
                          </p>
                          {provider.user.configured && (
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => void testKey(provider.provider)}
                                disabled={busy}
                              >
                                <Wrench className="size-3" />
                                {isTesting
                                  ? t("connections:byok.testing")
                                  : t("connections:byok.test")}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => void removeKey(provider.provider)}
                                disabled={busy}
                              >
                                <Trash2 className="size-3" />
                                {isRemoving
                                  ? t("connections:byok.removing")
                                  : t("connections:byok.remove")}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CollapsibleContent>
                  </article>
                </Collapsible>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
